import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { CountryCode } from 'aws-sdk/clients/route53domains';
import classNames from 'classnames';

import {
  StoreSwitcherButtonContainer,
} from '../../containers/StoreSwitcherButtonContainer/StoreSwitcherButtonContainer';
import { storeSwitcherHooks } from '../../hooks/storeSwitcherHooks/storeSwitcherHooks';
import { serverUtils } from '../../utils/serverUtils/serverUtils';
import { Button, ButtonFit, ButtonIntent } from '../Button/Button';
import { LayoutWrapper } from '../LayoutWrapper/LayoutWrapper';
import { Typography } from '../Typography/Typography';
import { Close } from '../icons';
import styles from './StoreSwitcherBlock.module.css';

const messages = defineMessages({
  topText: {
    id: 'src.components.StoreSwitcherBlock.topText',
    defaultMessage: 'You are visiting a website that does not deliver in the country where you are now. Want to exchange to your local store?',
  },
  continueButton: {
    id: 'src.components.StoreSwitcherBlock.continueButton',
    defaultMessage: 'Continue',
  },
});

export const SWITCHER_KEY = `${CONFIG.basePath}-switcher-expires`;

export type UseUpdateInitialToShow = {
  isFirstTime: boolean;
  isShowInitialState: boolean;
  setIsFirstTime: React.Dispatch<React.SetStateAction<boolean>>;
  setIsShow: React.Dispatch<React.SetStateAction<boolean>>;
};

export const hooks = {
  useGetStates(isShowInitialState: boolean) {
    const [isShow, setIsShow] = React.useState<boolean>(isShowInitialState);
    const [isFirstTime, setIsFirstTime] = React.useState<boolean>(true);
    return {
      isShow,
      setIsShow,
      isFirstTime,
      setIsFirstTime,
    };
  },
  useToShow(countryCode: CountryCode | null) {
    return React.useMemo(() => {
      const expiringDateString = !serverUtils.isServerSide() && localStorage.getItem(SWITCHER_KEY);
      const isDateExpired = expiringDateString ? new Date(expiringDateString) < new Date() : true;
      const isCorrectCountry = !countryCode || countryCode === CONFIG.cadeaubonGraphql.country;
      return isCorrectCountry ? false : isDateExpired;
    }, [countryCode]);
  },
  useCloseSwitcher(
    setIsShow: React.Dispatch<React.SetStateAction<boolean>>,
    setHeight: React.Dispatch<React.SetStateAction<number>>,
  ) {
    return React.useCallback(() => {
      const date = new Date();
      date.setDate(date.getDate() + 2);
      localStorage.setItem(SWITCHER_KEY, date.toString());
      setIsShow(false);
      setHeight(0);
    }, [setHeight, setIsShow]);
  },
  useUpdateInitialToShow({
    isFirstTime,
    setIsFirstTime,
    isShowInitialState,
    setIsShow,
  }: UseUpdateInitialToShow) {
    React.useEffect(() => {
      if (isShowInitialState && isFirstTime) {
        setIsFirstTime(false);
        setIsShow(true);
      }
    }, [isFirstTime, isShowInitialState, setIsFirstTime, setIsShow]);
  },
};

export type Props = {
  setHeight: React.Dispatch<React.SetStateAction<number>>;
};

export const StoreSwitcherBlock = React.memo(
  React.forwardRef(({ setHeight }: Props, ref: React.ForwardedRef<HTMLDivElement>) => {
    const { countryCode } = storeSwitcherHooks.useIpLocation();
    const isShowInitialState = hooks.useToShow(countryCode);
    const {
      isShow,
      setIsShow,
      isFirstTime,
      setIsFirstTime,
    } = hooks.useGetStates(isShowInitialState);
    hooks.useUpdateInitialToShow({
      isFirstTime,
      setIsFirstTime,
      isShowInitialState,
      setIsShow,
    });
    const closeSwitcherHandler = hooks.useCloseSwitcher(setIsShow, setHeight);
    const storeSwitcherButtonLabel = storeSwitcherHooks.useGetStoreSwitcherButtonLabel({});
    const onStoreRedirect = storeSwitcherHooks.useStoreRedirect();
    const initialCountryCode = storeSwitcherHooks.useGetInitialCountryCode();
    return (
      <div
        ref={ref}
        className={classNames(styles.storeSwitcherBlock, {
          [styles.hidden]: !isShow || !initialCountryCode,
        })}
      >
        <LayoutWrapper>
          <div className={styles.topBlock}>
            <Typography variant='bodySubText1' className={styles.text}>
              <FormattedMessage {...messages.topText} />
            </Typography>
            <Close
              className={styles.icon}
              onClick={closeSwitcherHandler}
              width={16}
              height={16}
              color={'var(--store-switcher-block-icon)'}
            />
          </div>
          <div className={styles.bottomBlock}>
            <StoreSwitcherButtonContainer label={storeSwitcherButtonLabel} />
            <Button
              intent={ButtonIntent.secondary}
              fit={ButtonFit.Inline}
              className={styles.continueBtn}
              onClick={onStoreRedirect}
            >
              <FormattedMessage {...messages.continueButton} />
            </Button>
          </div>
        </LayoutWrapper>
      </div>
    );
  }),
);

StoreSwitcherBlock.displayName = 'StoreSwitcherBlock';
